'use client'

import { useEffect, useRef, useState } from 'react'
import { Share2 } from 'lucide-react'

/**
 * Generic multi-platform share dropdown for the marketing dashboards. Downloads the page-faithful
 * screenshot (imgPath, e.g. /api/shot/earnings-calendar.png) for X/StockTwits/Instagram (which can't
 * auto-attach media) and opens the share intent with the page URL for Reddit/Facebook/LinkedIn (which
 * unfurl the og:image). Copy grabs the caption + URL.
 */
export default function ShareMenu({ url, imgPath, caption, title, label = 'Share', copyText }: {
  url: string; imgPath: string; caption: string; title: string; label?: string
  /** When provided, adds a "Copy text (full board)" item that copies this — the whole board as a
   *  pasteable post body (vs the short "Copy caption" teaser). */
  copyText?: string
}) {
  const [open, setOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  const [copiedText, setCopiedText] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false) }
    document.addEventListener('mousedown', onDoc); return () => document.removeEventListener('mousedown', onDoc)
  }, [])
  const download = () => {
    try { const a = document.createElement('a'); a.href = imgPath; a.download = (imgPath.split('/').pop() || 'share.png').split('?')[0]; document.body.appendChild(a); a.click(); a.remove() } catch { /* ignore */ }
  }
  const share = (k: string) => {
    const u = encodeURIComponent(url)
    switch (k) {
      case 'x': download(); window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(caption)}`, '_blank', 'noopener,noreferrer'); break
      case 'reddit': window.open(`https://www.reddit.com/submit?url=${u}&title=${encodeURIComponent(title)}`, '_blank', 'noopener,noreferrer'); break
      case 'facebook': window.open(`https://www.facebook.com/sharer/sharer.php?u=${u}`, '_blank', 'noopener,noreferrer'); break
      case 'linkedin': window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${u}`, '_blank', 'noopener,noreferrer'); break
      case 'stocktwits': download(); window.open('https://stocktwits.com/', '_blank', 'noopener,noreferrer'); break
      case 'download': download(); break
      // No URL appended — outbound links get throttled by social algorithms (owner 2026-07-14). The
      // caption copies clean; Reddit/FB/LinkedIn intents above still pass the URL for og-image unfurl.
      case 'copy': try { navigator.clipboard.writeText(caption) } catch { /* ignore */ } break
      case 'copytext': try { navigator.clipboard.writeText(copyText ?? '') } catch { /* ignore */ } break
    }
  }
  // Native share sheet on touch devices: the OS picker with the user's REAL apps (Messages, WhatsApp, the
  // Twitter app...), one tap, and the shared URL unfurls the og:image card. Far better than a web-intent
  // list on a phone. Desktop (no navigator.share, or a mouse) keeps the rich dropdown below. Fully
  // feature-detected + gesture-triggered, so a browser without it just falls through to the menu.
  const onShareClick = () => {
    const nav: (Navigator & { share?: (d: ShareData) => Promise<void> }) | undefined =
      typeof navigator !== 'undefined' ? navigator : undefined
    const touch = typeof matchMedia === 'function'
      ? matchMedia('(pointer: coarse)').matches
      : (nav?.maxTouchPoints ?? 0) > 0
    if (nav?.share && touch) {
      nav.share({ title, text: caption, url }).catch(() => { /* user dismissed the sheet - no-op */ })
      return
    }
    setOpen((o) => !o)
  }
  const items: { key: string; label: string; color: string; hint?: string }[] = [
    { key: 'x', label: 'X (Twitter)', color: '#e7e9ea', hint: '+ image' },
    { key: 'reddit', label: 'Reddit', color: '#ff4500' },
    { key: 'facebook', label: 'Facebook', color: '#1877f2' },
    { key: 'linkedin', label: 'LinkedIn', color: '#0a66c2' },
    { key: 'stocktwits', label: 'StockTwits', color: '#40a5f7', hint: '+ image' },
    { key: 'download', label: 'Download image', color: '#a1a1aa', hint: 'IG, etc.' },
    ...(copyText ? [{ key: 'copytext', label: copiedText ? 'Copied!' : 'Copy text (full board)', color: '#34d399', hint: 'for a post' }] : []),
    { key: 'copy', label: copied ? 'Copied!' : 'Copy caption', color: '#a1a1aa' },
  ]
  return (
    <div ref={ref} className="relative">
      <button onClick={onShareClick}
        className="inline-flex items-center gap-1.5 rounded-lg border border-sky-300 dark:border-sky-500/30 bg-sky-50 dark:bg-sky-500/10 px-2.5 py-1.5 text-xs font-semibold text-sky-700 dark:text-sky-300 transition-colors hover:bg-sky-100 dark:hover:bg-sky-500/20">
        <Share2 className="h-3.5 w-3.5" /> {label}
      </button>
      {open && (
        <div className="absolute right-0 top-full z-40 mt-1.5 w-56 rounded-xl border border-border bg-card/[0.98] p-1.5 shadow-2xl shadow-black/60 backdrop-blur">
          {items.map((it) => (
            <button key={it.key} onClick={() => { share(it.key); if (it.key === 'copy') { setCopied(true); setTimeout(() => setCopied(false), 1500) } else if (it.key === 'copytext') { setCopiedText(true); setTimeout(() => setCopiedText(false), 1500) } else setOpen(false) }}
              className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[13px] text-foreground/80 transition-colors hover:bg-accent dark:hover:bg-white/5">
              <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: it.color }} />
              <span className="font-semibold">{it.label}</span>
              {it.hint && <span className="ml-auto text-[10px] text-muted-foreground">{it.hint}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
